import store from "@/store";
import trackResponse from "@/utils/miscellaneous/track-response";
import router from "@/router/index";
import qs from "qs";

const initiatorWhitelisted = ["global"]

const generateURL = ({
  url,
  version,
  params,
  method,
  isURLFull,
}) => {
  let result = isURLFull ? url : `${process.env.VUE_APP_API_URL}v${version}/${url}`
  const requiredParams = method.toLowerCase() === "get" ? removeEmptyParams(params) : params

  if (Object.keys(requiredParams).length) {
    // to avoid duplication for ? mark
    result = `${result}${result.includes("?") ? "&" : "?"}${qs.stringify(requiredParams, { encode: true })}`
  }

  return result
}

const generateInitiator = ({ initiator }) => {
  if (process.env.NODE_ENV === "development" && initiator && !initiatorWhitelisted.includes(initiator)) {
    trackResponse({
      type: "error",
      msg: "Please add valid initiator",
    });
    return
  }

  return `${initiator || router.currentRoute.name}`
}

const getRequestId = ({
  initiator,
  id,
}) => `${initiator}/${id}`

const handleRequestInterceptor = ({
  requestId,
  controller,
  blockScreen,
  initiator,
  cancelWhenLeaveInitiator,
  cancelAllPendingRequest,
}) => {
  /**
   * if the requestId already exists, cancel it before using the new one
   */

  const selectedPendingRequest = store.state.Loader.pendingRequests.find((el) => el.id === requestId)

  if (selectedPendingRequest) {
    selectedPendingRequest.controller.abort({
      requestId,
      message: "abort",
      isClientError: true,
    })
  }

  const pendingRequests = [
    ...store.state.Loader.pendingRequests.filter((el) => el.id !== requestId),
    {
      id: requestId,
      controller,
      initiator,
      cancelWhenLeaveInitiator: cancelWhenLeaveInitiator === undefined ? !initiatorWhitelisted.includes(initiator) : cancelWhenLeaveInitiator,
      blockScreen,
    },
  ]

  store.dispatch("Loader/setPendingRequests", pendingRequests, { root: true })

  if (blockScreen) {
    store.dispatch("Loader/setIsLoading", true, { root: true })
  }

  /**
   * some requests require canceling any other pending requests e.g. login as
   */
  if (cancelAllPendingRequest) {
    store.state.Loader.pendingRequests.forEach((el) => {
      if (el.id !== requestId) {
        el.controller.abort({
          requestId: el.id,
          message: "abort",
          isClientError: true,
        })
      }
    })
  }
}

const handleResponseInterceptor = ({ requestId }) => {
  const pendingRequests = store.state.Loader.pendingRequests.filter((request) => request.id !== requestId)

  if (pendingRequests.filter((req) => req.blockScreen).length === 0) {
    store.dispatch("Loader/setIsLoading", false, { root: true })
  }

  store.dispatch("Loader/setPendingRequests", pendingRequests, { root: true })
}

const handleResponseErrorsInterceptor = ({
  requestId,
  errors,
  showGlobalAlert,
  disableDefaultErrorHandlingAt,
  url,
  params,
  body,
  excludedLogFields,
  disableLoggingAt,
}) => {
  store.dispatch("ResponseHandling/resetErrorsState", null, { root: true })
  const { pendingRequests } = store.state.Loader

  const mutatedPendingRequests = pendingRequests.filter((request) => request.id !== requestId)
  store.dispatch("Loader/setPendingRequests", mutatedPendingRequests, { root: true })

  store.dispatch("ResponseHandling/handleErrors",
    {
      errors,
      showGlobalAlert,
      disableDefaultErrorHandlingAt,
      url,
      params,
      body,
      excludedLogFields,
      disableLoggingAt,
    }, { root: true })

  const numOfBlockedScreenRequests = store.state.Loader.pendingRequests.filter((req) => req.blockScreen).length
  if (numOfBlockedScreenRequests === 0 && store.state.Loader.isLoading) {
    store.dispatch("Loader/setIsLoading", false, { root: true })
  }
}

const removeEmptyParams = (params) => {
  const result = params

  Object.keys(params)
    .forEach((key) => (params[key] === "" && key !== "q" ? delete result[key] : null))

  return result
}

const isValidRequest = ({
  requestId,
  url,
}) => {
  /**
   * requestId is required,
   * if we don't add requestId to each api the api will be canceled.
   * we avoid adding '/' symbol because this symbol we already add when generating requestId to define the route name
   * ex: overview/getChartData => routeName/requestId (for readability wise)
   */
  if ((!requestId || requestId.includes("/")) && process.env.NODE_ENV === "development") {
    trackResponse({
      type: "error",
      msg: `Please add the request Id to this ( ${url} ) without any ( / ) symbol`,
    });

    return false
  }
  /**
   * In most cases, we send the dynamic part of the URL through the router, and suddenly the value gets undefined, null and so on...,
   * test case: we send apiRouteKey, if the user quickly presses the back button the value will be undefined.
   */
  const isNotValidUrl = url.split("/")
    .some((el) => ["undefined", "null", "nan"].includes(el.toLowerCase()))
  if (isNotValidUrl && process.env.NODE_ENV === "development") {
    trackResponse({
      type: "error",
      msg: `The ${url} request has an unexpected url`,
    });

    return false
  }

  if (url.includes("?")) {
    trackResponse({
      type: "error",
      msg: `The ${url} url contains ? mark`,
    });

    return false
  }

  return true
}

export {
  initiatorWhitelisted,
  generateURL,
  handleRequestInterceptor,
  handleResponseInterceptor,
  handleResponseErrorsInterceptor,
  generateInitiator,
  getRequestId,
  isValidRequest,
}
